import { Injectable } from '@angular/core';
import { convertToParamMap, ParamMap, Params } from '@angular/router';
import { ListInterface } from '@core/interfaces/list/list.interface';
import { MixedObject } from '@core/interfaces/mixed-object';
import { FilterType } from '@core/models';
import { BehaviorSubject, Observable } from 'rxjs';

declare type ArrayParams = (string | number)[];

const toStringArray = (params: Params, controlType: string): string[] | undefined => {
  const map = convertToParamMap(params);
  return map.getAll(controlType);
};

const addToArrayParam = (params: Params, controlType: string, categoryId: number): ArrayParams => {
  const values = toStringArray(params, controlType);
  return values === undefined ? [categoryId] : [...values, categoryId];
};

const removeToArrayParam = (params: Params, controlType: string, categoryId: number): ArrayParams => {
  const values = toStringArray(params, controlType);
  const idx = values.indexOf(categoryId.toString());
  const _removed = values[idx];
  return values.filter(_val => _val !== _removed);
};

const removeAllKeysToArrayParam = (params: Params, controlType: string): ArrayParams => {
  return removeToArrayParam(params, controlType, -1); // reset array
};

const defaultFilterSettings = {isSingleStateFilters: false};

export interface FilterSettings {
  isSingleStateFilters: boolean;
  orientation?: string;
  showClearFiltersButton?: boolean;
}

@Injectable()
export class FiltersService {
  paramsGlobalClean: boolean;
  public filtersTo = new BehaviorSubject<Params>(null);
  paramsSelectFiters: any;
  public listCategory = new BehaviorSubject<any[]>([]);
  public listFilterSlidable = new BehaviorSubject<ListInterface[]>([]);
  public selectLastFilter: string;
  private _params: Params;
  private _settings: FilterSettings = defaultFilterSettings;
  private filtersFrom = new BehaviorSubject<ParamMap>(null);
  filtersFrom$ = this.filtersFrom.asObservable();
  parentBlock: any;
  marginBottom: string;
  sizeBlock: string;
  sortingParse: any;

  get hasSearch(): boolean {
    return this.filtersFrom.value?.has('search');
  }

  get filtersToChanged(): Observable<Params> {
    return this.filtersTo.asObservable();
  }

  public updateFiltersFrom(param: Params): void {
    // Update internal parameters state and notify subscribers
    this._params = param;
    this.filtersFrom.next(convertToParamMap(param));
  }

  public goTo(params: Params) {
    // Only add availability and sorting if they're not already in params.
    // `_params` bywa `undefined` — serwis nie jest singletonem i instancja
    // panelu filtrów nigdy nie przechodzi przez `updateFiltersFrom()`.
    const newCopy = {
      ...params,
      availability: params[FilterType.AVAILABILITY] !== undefined ? params[FilterType.AVAILABILITY] : this._params?.[FilterType.AVAILABILITY],
      sorting: params[FilterType.SORTING] !== undefined ? params[FilterType.SORTING] : this._params?.[FilterType.SORTING],
    };

    this.filtersTo.next(newCopy);
  }


  public updateParams(filterState: boolean, controlType: FilterType, categoryId: any) {
    let _filterStateResult: ArrayParams = null;
    const {isSingleStateFilters} = this._settings;

    // unselect params
    if (!isSingleStateFilters && !filterState) {
      _filterStateResult = removeToArrayParam(this._params, controlType, categoryId);
    }

    // apply single param
    if (isSingleStateFilters && filterState) {

      // remove all keys from params
      const _singleStateResult = removeAllKeysToArrayParam(this._params, controlType);

      // add a single
      _filterStateResult = addToArrayParam(_singleStateResult, controlType, categoryId);
    }

    // apply multiple params
    if (!isSingleStateFilters && filterState) {
      // if ture and not single select
      _filterStateResult = filterState
        ? addToArrayParam(this._params, controlType, categoryId)
        : removeToArrayParam(this._params, controlType, categoryId);
    }

    // reset filter state for availability
    if (controlType === FilterType.AVAILABILITY || controlType === FilterType.SORTING) {
      _filterStateResult = [categoryId];
    }

    const newCopy = {...this._params, [controlType]: _filterStateResult};
    this.filtersTo.next(newCopy);
  }

  search(searchParams: MixedObject) {
    // Merge new search parameters with existing parameters
    const newCopy = {...this._params, ...searchParams};

    // Convert extended_search to extendedSearch for backend compatibility
    if (newCopy.extended_search !== undefined) {
      newCopy.extendedSearch = newCopy.extended_search;
      delete newCopy.extended_search;
    }

    // Emit updated parameters to trigger navigation and data refresh
    this.filtersTo.next(newCopy);
  }

  initSettings(settings: FilterSettings) {
    this._settings = settings;
  }
}
